import { z } from 'zod'

/**
 * The two languages, and the three things both sides of the wire need to agree on:
 * which locales exist, how a template turns into a sentence, and how a browser's
 * `Accept-Language` maps onto one of ours.
 *
 * This lives in contracts rather than in the client because the server also writes
 * prose — push notification payloads and API error messages are rendered against a
 * recipient, not against whoever happens to be reading the code.
 *
 * Numbers are deliberately *not* localised. Nepali trade runs on Arabic numerals
 * and `en-IN` digit grouping (see money.ts); a price rendered १,२३४ would be read
 * slower by the audience than the one they see on every invoice today.
 */

export const LOCALES = ['en', 'ne'] as const
export type Locale = (typeof LOCALES)[number]
export const localeSchema = z.enum(LOCALES)

export const DEFAULT_LOCALE: Locale = 'en'

/** Each language named in itself — the only form that helps someone who cannot read the other. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  ne: 'नेपाली',
}

/** What goes in `<html lang>` and in the `Content-Language` header. */
export const LOCALE_TAGS: Record<Locale, string> = {
  en: 'en',
  ne: 'ne-NP',
}

/** A value that exists in both languages. Type-level insurance against a half-filled catalog. */
export type Localized<T> = Record<Locale, T>

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/**
 * Best match from an `Accept-Language` header, or null if it asks for nothing we
 * have. Quality values are honoured because a Nepali phone commonly sends
 * `ne,en-US;q=0.9` and answering that in English would be ignoring a plain request.
 */
export function parseAcceptLanguage(header: string | null | undefined): Locale | null {
  if (!header) return null

  const ranked = header
    .split(',')
    .map((part) => {
      const [tag = '', ...rest] = part.trim().split(';')
      const q = rest.find((p) => p.trim().startsWith('q='))
      const quality = q ? Number.parseFloat(q.trim().slice(2)) : 1
      return { tag: tag.trim().toLowerCase(), quality: Number.isFinite(quality) ? quality : 0 }
    })
    .filter((entry) => entry.tag !== '' && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality)

  for (const { tag } of ranked) {
    // `ne-NP` and `ne` are the same request as far as this app is concerned.
    const base = tag.split('-')[0]
    if (base === 'ne') return 'ne'
    if (base === 'en') return 'en'
    if (tag === '*') return null
  }
  return null
}

/**
 * `{name}` substitution, and nothing else.
 *
 * No expressions, no formatting directives, no ICU. A translator editing the Nepali
 * catalog is a shopkeeper's colleague, not a programmer, and every feature added
 * here is another way for them to break a screen. A placeholder with no matching
 * param is left visible on purpose — a missing name is a bug worth seeing rather
 * than an empty gap that reads as finished text.
 */
export function interpolate(
  template: string,
  params?: Readonly<Record<string, string | number>>,
): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => {
    const value = params[key]
    return value === undefined ? whole : String(value)
  })
}

/**
 * English and Nepali share the same plural split — one versus everything else — so
 * a two-form selector covers both. Zero takes the plural form in each.
 */
export function plural(count: number, forms: { one: string; other: string }): string {
  return count === 1 ? forms.one : forms.other
}
